import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Fade,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { MessageDeliveryType } from "../../App/Enums/Message";
import { useGetCandidatesWithMessageInfo } from "../../App/Queries/MessageQueries";
import { useUserProfileStore } from "../../App/State/Stores/UserProfileStore";
import { ICandidateWithMessageInfo } from "../../App/Typings/MessageText";
import { useFilterContext } from "../State/FilterContext";
import { useGroupsContext } from "../State/GroupsContext";
import { useMessagesContext } from "../State/MessagesContext";
import { candidateInitials } from "../Utils";

const pageSize = 8;

export default function ContactList({ type }: { type: MessageDeliveryType }) {
  const { getUserAdjustedDateFromNow } = useUserProfileStore();
  const { selectedGroupIds } = useGroupsContext();
  const { selectedCandidate, setSelectedCandidate } = useMessagesContext();
  const { search, page, setPage, unreadOnly } = useFilterContext();

  const { isFetched: isCandidateDataFetched, data } = useGetCandidatesWithMessageInfo(
    page,
    pageSize,
    type,
    search,
    selectedGroupIds,
    unreadOnly,
  );

  const candidates = useMemo(() => filterCandidates(search, data?.items ?? []), [search, data]);

  useEffect(() => {
    if (isCandidateDataFetched && data?.items && data.items.length > 0 && !selectedCandidate) {
      setSelectedCandidate(data.items[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCandidateDataFetched, data?.items, selectedCandidate?.id]);

  return (
    <Box sx={{ border: "#ccc 1px solid", borderRadius: 1, p: 1, minHeight: { lg: 550 }, height: "100%" }}>
      <Stack display="column" alignItems="stretch" sx={{ height: "100%" }}>
        {!isCandidateDataFetched || !data ? (
          <Box sx={{ minHeight: "550px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Fade timeout={350} in>
              <List sx={{ width: "100%", flexGrow: 1 }} data-test="candidate-email-list">
                {candidates &&
                  candidates?.map((c) => {
                    return (
                      <React.Fragment key={`candidate-email-list-${c.id}`}>
                        <ListItemButton
                          data-test={`candidate-email-list-${c.id}`}
                          onClick={() => setSelectedCandidate(c)}
                          selected={selectedCandidate?.id === c.id}
                          sx={{
                            borderLeft: c.hasUnreadMessages ? "4px solid " : "none",
                            borderColor: "captivatePrimary.main",
                          }}
                        >
                          <ListItemAvatar sx={{ marginLeft: c.hasUnreadMessages ? "-4px" : 0 }}>
                            <Avatar>{candidateInitials(c)}</Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            sx={{
                              "> .MuiListItemText-primary": {
                                overflowWrap: "break-word",
                                fontWeight: c.hasUnreadMessages ? 600 : 300,
                              },
                              "> .MuiListItemText-secondary": {
                                margin: 0,
                              },
                            }}
                            primary={`${c.firstName} ${c.lastName}`}
                            secondary={
                              type === MessageDeliveryType.Email
                                ? c.lastEmailInteraction
                                  ? getUserAdjustedDateFromNow(c.lastEmailInteraction)
                                  : ""
                                : c.lastTextInteraction
                                  ? getUserAdjustedDateFromNow(c.lastTextInteraction)
                                  : ""
                            }
                          />
                        </ListItemButton>
                        <Divider />
                      </React.Fragment>
                    );
                  })}
                {!candidates ||
                  (candidates.length <= 0 && (
                    <ListItem>
                      <Typography sx={{ margin: "50px auto" }}>No candidates to display.</Typography>
                    </ListItem>
                  ))}
              </List>
            </Fade>
            <Pagination
              sx={{ alignSelf: "center" }}
              size="small"
              count={data?.totalPages ?? 1}
              onChange={(_ev, number) => {
                setPage(number);
              }}
              page={page}
            />
          </>
        )}
      </Stack>
    </Box>
  );
}

const filterCandidates = (search: string, candidates: ICandidateWithMessageInfo[]) => {
  if (search) {
    return candidates.filter(
      (c) => (c.firstName.toLowerCase() + " " + c.lastName.toLowerCase()).indexOf(search) >= 0,
    );
  }

  return candidates;
};
